import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import { ayurvedaService } from '../../services/ayurvedaService';
import { wellnessService } from '../../services/wellnessService';

const TreatmentsList = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const categoryRef = useRef(null);
  const [treatments, setTreatments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  // Detect category from URL parameter and auto-select it
  useEffect(() => {
    const categoryIdFromUrl = searchParams.get('category') || location?.state?.categoryId;

    if (categoryIdFromUrl && categories?.length > 0) {
      // Check if the category exists in the loaded categories
      const categoryExists = categories?.some(cat => cat?.id === categoryIdFromUrl);

      if (categoryExists) {
        setSelectedCategory(categoryIdFromUrl);

        // Scroll to the category filter section after a short delay
        setTimeout(() => {
          if (categoryRef?.current) {
            categoryRef?.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }, 500);
      }
    }
  }, [searchParams, location?.state, categories]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load categories
      const categoriesResult = await ayurvedaService?.getTreatmentCategories();
      if (categoriesResult?.error) {
        setError('Failed to load treatment categories');
        return;
      }
      setCategories(categoriesResult?.data || []);

      // Load treatments
      const treatmentsResult = await ayurvedaService?.getTreatments();
      if (treatmentsResult?.error) {
        setError('Failed to load treatments');
        return;
      }
      setTreatments(treatmentsResult?.data || []);

    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const filteredTreatments = selectedCategory === 'all'
    ? treatments
    : treatments?.filter(treatment => treatment?.category_id === selectedCategory);

  const flagshipTreatments = treatments?.filter(treatment => treatment?.is_flagship === true);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading treatments...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Ayurvedic Treatments - Sri Dhanwantari AyurVaidik Parishkar Kendra</title>
        <meta
          name="description"
          content="Comprehensive Ayurvedic treatments for chronic conditions including Psoriasis, PCOD, joint disorders, and digestive health. Authentic healing with personalized care."
        />
        <meta
          name="keywords"
          content="ayurvedic treatments, psoriasis cure, PCOD treatment, joint pain relief, chronic disease, natural healing, panchakarma therapy"
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-24">
          {/* Hero Section */}
          <section className="relative py-20 overflow-hidden">
            {/* Background Image with Overlay */}
            <div className="absolute inset-0 z-0">
              <img
                src="/assets/images/treatments-background.webp"
                alt="Ayurvedic Wellness Background"
                className="w-full h-full object-cover"
              />
              {/* Semi-transparent overlay for text readability */}
              <div className="absolute inset-0 bg-gradient-to-br from-background/80 via-background/70 to-background/60"></div>
            </div>

            <div className="container mx-auto px-4 relative z-10">

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center max-w-3xl mx-auto"
              >

                <h1 className="font-headline text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-primary">
                  Authentic Ayurvedic Treatments
                </h1>
                <p className="text-lg md:text-xl text-muted-foreground mb-8">
                  Comprehensive healing solutions for chronic conditions through time-tested Ayurvedic principles,
                  personalized therapy, and holistic wellness approaches.
                </p>
              </motion.div>

            </div>
          </section>

          {/* Flagship Treatments */}
          {flagshipTreatments?.length > 0 && (
            <section className="py-16">
              <div className="container mx-auto px-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="text-center mb-12"
                >
                  <h2 className="font-headline text-3xl md:text-4xl font-bold mb-4 text-primary">
                    Flagship Treatments
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Our specialized treatment programs with proven success rates and comprehensive care protocols.
                  </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {flagshipTreatments?.map((treatment, index) => (
                    <motion.div
                      key={treatment?.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.3 + index * 0.1 }}
                      className="glass-card-readable p-6 interactive-card group"
                    >
                      <div className="relative overflow-hidden rounded-lg mb-6 h-48">
                        <Image
                          src={treatment?.main_image_url || "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=600&h=400&fit=crop"}
                          alt={`${treatment?.title} - Comprehensive Ayurvedic treatment at Sri Dhanwantari AyurVaidik Parishkar Kendra`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-4 right-4">
                          <span className="bg-accent text-white px-3 py-1 rounded-full text-sm font-medium">
                            Flagship
                          </span>
                        </div>
                      </div>

                      <h3 className="font-headline text-xl font-bold mb-3 text-primary group-hover:text-accent transition-colors">
                        {treatment?.title}
                      </h3>

                      <p className="text-muted-foreground mb-4 line-clamp-3">
                        {treatment?.short_description}
                      </p>

                      {treatment?.success_rate_percentage && (
                        <div className="flex items-center gap-2 mb-4">
                          <Icon name="TrendingUp" size={16} className="text-success" />
                          <span className="text-success font-medium">
                            {treatment?.success_rate_percentage}% Success Rate
                          </span>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 mb-6">
                        {treatment?.tags?.slice(0, 3)?.map((tag, tagIndex) => (
                          <span
                            key={tagIndex}
                            className="bg-secondary/20 text-primary px-2 py-1 rounded text-sm"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      <Link to={`/treatments/${treatment?.slug}`} className="w-full">
                        <Button
                          variant="default"
                          className="ayurveda-gradient text-white font-medium"
                          iconName="ArrowRight"
                          iconPosition="right"
                        >
                          Learn More
                        </Button>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Category Filter */}
          <section ref={categoryRef} className="py-16 bg-muted/30">
            <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-center mb-12"
              >
                <h2 className="font-headline text-3xl md:text-4xl font-bold mb-4 text-primary">
                  All Treatments
                </h2>
                <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
                  Browse our comprehensive range of Ayurvedic treatments organized by specialty areas.
                </p>

                {/* Category Filters */}
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    variant={selectedCategory === 'all' ? 'default' : 'outline'}
                    className={selectedCategory === 'all' ? 'ayurveda-gradient text-white' : 'border-primary/30 text-primary hover:bg-primary/10'}
                    onClick={() => setSelectedCategory('all')}
                  >
                    All Categories
                  </Button>
                  {categories?.map((category) => (
                    <Button
                      key={category?.id}
                      variant={selectedCategory === category?.id ? 'default' : 'outline'}
                      className={selectedCategory === category?.id ? 'ayurveda-gradient text-white' : 'border-primary/30 text-primary hover:bg-primary/10'}
                      onClick={() => setSelectedCategory(category?.id)}
                    >
                      {category?.name}
                    </Button>
                  ))}
                </div>
              </motion.div>

              {error ? (
                <div className="text-center py-12">
                  <div className="bg-error/10 border border-error/20 rounded-lg p-6 max-w-md mx-auto">
                    <Icon name="AlertCircle" size={24} className="text-error mx-auto mb-4" />
                    <p className="text-error font-medium mb-2">Unable to load treatments</p>
                    <p className="text-muted-foreground text-sm mb-4">{error}</p>
                    <Button
                      variant="outline"
                      onClick={loadData}
                      className="border-error text-error hover:bg-error/10"
                    >
                      Try Again
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredTreatments?.map((treatment, index) => (
                    <motion.div
                      key={treatment?.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.5 + index * 0.1 }}
                      className="glass-card p-6 interactive-card group"
                    >
                      <div className="relative overflow-hidden rounded-lg mb-4 h-42">
                        <Image
                          src={treatment?.main_image_url || "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=600&h=400&fit=crop"}
                          alt={`${treatment?.title} - Ayurvedic treatment for ${treatment?.category?.name} conditions`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        {treatment?.is_featured && (
                          <div className="absolute top-2 right-2">
                            <span className="bg-secondary text-primary px-2 py-1 rounded text-xs font-medium">
                              Featured
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="mb-2">
                        <span className="text-xs text-accent font-medium uppercase tracking-wide">
                          {treatment?.category?.name}
                        </span>
                      </div>

                      <h3 className="font-headline text-lg font-bold mb-2 text-primary group-hover:text-accent transition-colors">
                        {treatment?.title}
                      </h3>

                      <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                        {treatment?.short_description}
                      </p>

                      <Link to={`/treatments/${treatment?.slug}`}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full border-primary/30 text-primary hover:bg-primary/10"
                          iconName="ArrowRight"
                          iconPosition="right"
                        >
                          View Details
                        </Button>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}

              {filteredTreatments?.length === 0 && !error && (
                <div className="text-center py-12">
                  <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No treatments found</h3>
                  <p className="text-muted-foreground">
                    Try selecting a different category or check back later.
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 bg-gradient-to-br from-primary to-secondary">
            <div className="container mx-auto px-4 text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="max-w-2xl mx-auto"
              >
                <h2 className="font-headline text-3xl md:text-4xl font-bold mb-6 text-white">
                  Ready to Begin Your Healing Journey?
                </h2>
                <p className="text-white/90 mb-8 text-lg">
                  Book a consultation with our experienced Ayurvedic doctors and discover
                  the personalized treatment plan that's right for you.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/booking">
                    <Button
                      variant="outline"
                      size="lg"
                      className="bg-white text-primary border-white hover:bg-white/90 font-semibold px-8 py-4"
                      iconName="Calendar"
                      iconPosition="left"
                    >
                      Book Consultation
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button
                      variant="outline"
                      size="lg"
                      className="border-white text-white hover:bg-white/10 font-medium px-8 py-4"
                      iconName="Phone"
                      iconPosition="left"
                    >
                      Call Us Now
                    </Button>
                  </Link>
                </div>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default TreatmentsList;